import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { properties } from "../data/properties";
import PropertyCard from "../components/PropertyCard";
import { usePropertyFilters } from "../features/usePropertyFilters";
import "./Listings.css";

export default function Listings() {
  const { t } = useTranslation("listings");
  const [searchParams] = useSearchParams();
  const { filters, sort, filtered, handleFilterChange, handleSortChange, handleReset } =
    usePropertyFilters(properties);

  return (
    <main>
      <Helmet>
        <title>Property Listings in Dubai — ProNet Real Estate</title>
        <meta name="description" content="Browse 32+ curated properties for sale and rent in Dubai Marina, Downtown, Palm Jumeirah, and across the UAE. Filter by price, type, and location." />
        <link rel="canonical" href="https://pronet-realestate.com/listings" />
        {searchParams.toString() && <meta name="robots" content="noindex, follow" />}
        <meta property="og:title" content="Property Listings in Dubai — ProNet Real Estate" />
        <meta property="og:description" content="Browse 32+ curated properties for sale and rent in Dubai Marina, Downtown, Palm Jumeirah, and across the UAE." />
        <meta property="og:url" content="https://pronet-realestate.com/listings" />
        <meta property="og:type" content="website" />
      </Helmet>

      <section className="listings-hero">
        <div className="container">
          <h1 className="listings-title">{t("hero.title")}</h1>
          <p className="listings-sub">{t("hero.sub", { count: filtered.length })}</p>
        </div>
      </section>

      <div className="container listings-layout">
        <aside className="filters-sidebar">
          <div className="filters-header">
            <span className="filters-label">{t("filters.label")}</span>
            <button className="filters-reset" onClick={handleReset}>{t("filters.reset")}</button>
          </div>

          <div className="filter-group">
            <label>{t("filters.status")}</label>
            <div className="filter-options">
              {[["", t("filters.all")], ["sale", t("filters.sale")], ["rent", t("filters.rent")]].map(([val, label]) => (
                <button
                  key={val}
                  className={`filter-pill ${filters.priceType === val ? "active" : ""}`}
                  onClick={() => handleFilterChange("priceType", val)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <label>{t("filters.type")}</label>
            <select value={filters.type} onChange={(e) => handleFilterChange("type", e.target.value)}>
              <option value="">{t("filters.typeAll")}</option>
              <option value="Apartment">Apartment</option>
              <option value="Villa">Villa</option>
              <option value="Studio">Studio</option>
              <option value="Commercial">Commercial</option>
            </select>
          </div>

          <div className="filter-group">
            <label>{t("filters.emirates")}</label>
            <select value={filters.emirate} onChange={(e) => handleFilterChange("emirate", e.target.value)}>
              <option value="">{t("filters.any")}</option>
              <option value="Dubai">Dubai</option>
              <option value="Abu Dhabi">Abu Dhabi</option>
              <option value="Sharjah">Sharjah</option>
              <option value="Ajman">Ajman</option>
            </select>
          </div>

          <div className="filter-group">
            <label>{t("filters.beds")}</label>
            <div className="filter-options">
              {[["", t("filters.any")], ["1","1"], ["2","2"], ["3","3"], ["4","4+"]].map(([val, label]) => (
                <button
                  key={val}
                  className={`filter-pill ${filters.beds === val ? "active" : ""}`}
                  onClick={() => handleFilterChange("beds", val)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <label>{t("filters.maxPrice")}</label>
            <select value={filters.maxPrice} onChange={(e) => handleFilterChange("maxPrice", e.target.value)}>
              <option value="">{t("filters.any")}</option>
              <option value="1000000">1,000,000</option>
              <option value="2000000">2,000,000</option>
              <option value="5000000">5,000,000</option>
              <option value="10000000">10,000,000</option>
            </select>
          </div>
        </aside>

        <div className="listings-results">
          <div className="results-bar">
            <span className="results-count">{t("results.count", { count: filtered.length })}</span>
            <div className="results-sort">
              <label>{t("results.sort")}</label>
              <select value={sort} onChange={(e) => handleSortChange(e.target.value)}>
                <option value="default">{t("results.sortDefault")}</option>
                <option value="price-asc">{t("results.sortAsc")}</option>
                <option value="price-desc">{t("results.sortDesc")}</option>
              </select>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="no-results">
              <p>{t("results.noResults")}</p>
              <button className="filters-reset" onClick={handleReset}>{t("results.clearFilters")}</button>
            </div>
          ) : (
            <div className="results-grid">
              {filtered.map((p) => <PropertyCard key={p.id} property={p} />)}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
